import React, { useMemo, useState, useCallback } from "react";
import { useFlow } from "@/contexts/FlowContext";
import { BuildSteps, DeploySteps, Flows } from "@/types/flows";
import { RelabelConfirmationModal } from "@/components/flow/shared/RelabelConfirmationModal";
import {
    usePredictionsForFileAndFrameSegment,
    useCurrentWorkflow,
    useCurrentWorkspace,
    useIsTraining,
    useHasUnseenModelChange,
    useCurrentRapidData
} from "@/store";
import { hasTrainedModel } from "@/store/slices/modelSlice";
import { useAuth } from "@/contexts/AuthContext";
import { useDeployContext } from "@/contexts/DeployContext";
import { useComputedValues } from "@/contexts/ComputedValuesContext";
import { FeedbackForm } from "@/components/FeedbackForm";
import { useFlowState } from "@/hooks/useFlowState";
import { useNavigate } from "react-router-dom";
import { projectSelector } from "@rflocal/shared";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { TooltipContainer } from "@scripts/react/components/Tooltip";
import { useReviewBatchCount } from "@/hooks/useReviewBatchCount";
const STEP_BASE_CLASSES =
    "flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-normal font-sans text-tertiary";
const STEP_HOVER_CLASSES = "hover:bg-gray-100 hover:text-primary cursor-pointer";

interface StepProps {
    title: string;
    description?: string;
    isActive?: boolean;
    isCompleted?: boolean;
    isDisabled?: boolean;
    isClickable?: boolean;
    iconClass: string;
    tooltip?: string;
    onClick?: () => void;
}

const Step: React.FC<StepProps> = ({
    title,
    isActive = false,
    isDisabled = false,
    isClickable = false,
    iconClass,
    tooltip,
    onClick
}) => {
    const handleClick = () => {
        if (!isDisabled && isClickable && onClick) {
            onClick();
        }
    };

    const stepContent = (
        <div
            className={twClassNames(
                STEP_BASE_CLASSES,
                "text-sm transition-all duration-200",
                isDisabled ? "cursor-not-allowed opacity-50" : isClickable && STEP_HOVER_CLASSES,
                isActive ? "font-semibold text-brand" : "font-normal text-tertiary",
                isClickable && isActive && "hover:bg-tint hover:text-brand"
            )}
            onClick={handleClick}
        >
            <i className={iconClass} />
            <span>{title}</span>
        </div>
    );

    if (tooltip) {
        return (
            <TooltipContainer
                type="titleOnly"
                title={tooltip}
                placement="top"
                customButton={stepContent}
            />
        );
    }

    return stepContent;
};

export const FlowStepper = () => {
    const { task, flowLocked } = useFlow();
    const { setStep, flow, step } = useFlowState();
    const { currentInferenceSampledData } = useComputedValues();
    const predictionsForFileAndFrameSegment = usePredictionsForFileAndFrameSegment();
    const [showRelabelConfirmation, setShowRelabelConfirmation] = useState(false);

    const workflow = useCurrentWorkflow();
    const currentWorkspace = useCurrentWorkspace();

    // Deploy functionality hooks
    const {
        createRapidWorkflowDeploy,
        creatingRapidWorkflow,
        workflowCredentials,
        hasValidWorkflowCredentials
    } = useDeployContext();
    const { user } = useAuth();
    const navigate = useNavigate();

    // Handle deploy/integrate functionality
    const handleCreateRapidWorkflow = useCallback(async () => {
        if (!user) {
            setStep({ flow: Flows.BUILD, step: BuildSteps.PREVIEW });
            return;
        }

        try {
            const workflow = await createRapidWorkflowDeploy({
                task,
                workspaceId: currentWorkspace?.id,
                groundTruthData: currentInferenceSampledData
                // isSync: true // -- Set this to true if you want to run the whole first setup synchronously to debug
            });
            setStep({ flow: Flows.DEPLOY, step: DeploySteps.DEPLOY, workflowUrl: workflow.url });
        } catch (err) {
            console.error("FlowStepper handleCreateRapidWorkflow ", err);
            alert("There was an error creating your workflow. Please try again.");
            navigate("/");
        }
    }, [
        user,
        task,
        currentWorkspace,
        setStep,
        createRapidWorkflowDeploy,
        currentInferenceSampledData,
        navigate
    ]);

    const isPostBuild = flow === Flows.DEPLOY;
    const hasUnseenModelChange = useHasUnseenModelChange(user?.uid ?? "");
    const isTraining = useIsTraining();
    const { newDataReady } = useReviewBatchCount();
    const currentRapidData = useCurrentRapidData();
    const isEvaluateReady = useMemo(() => hasTrainedModel(currentRapidData), [currentRapidData]);

    const { steps, currentStep } = useMemo(() => {
        if (!flow) {
            return {
                steps: [{ title: "Loading...", description: "Loading steps" }],
                currentStep: 0
            };
        }

        if (flow === Flows.SETUP) {
            const setupSteps = [{ title: "Upload", description: "Upload your data" }];

            return { steps: setupSteps, currentStep: 0 };
        }

        if (flow === Flows.BUILD) {
            const flowSteps = [
                { title: "Build", description: "Build your model" },
                { title: "Review", description: "Preview your model" },
                { title: "Use", description: "Integrate your model" }
            ];

            const stepIndexMap = {
                [BuildSteps.PROMPT]: 0,
                [BuildSteps.REVIEW]: 1,
                [BuildSteps.PREVIEW]: 2
            };

            return {
                steps: flowSteps,
                currentStep: step
                    ? stepIndexMap[step as Exclude<BuildSteps, typeof BuildSteps.UPLOAD>]
                    : 0
            };
        }

        if (flow === Flows.DEPLOY) {
            const deployModelSteps = [
                { title: "Use", description: "Use your model" },
                { title: "Improve", description: "Improve your model" },
                { title: "Evaluate", description: "Evaluate your model" }
            ];
            const deployStepIndexMap: Record<string, number> = {
                [DeploySteps.DEPLOY]: 0,
                [DeploySteps.IMPROVE]: 1,
                [DeploySteps.MODEL]: 2
            };
            return {
                steps: deployModelSteps,
                currentStep: step ? (deployStepIndexMap[step] ?? 0) : 0
            };
        }

        if (flow === Flows.SHARE) {
            return {
                steps: [{ title: "Run the Model", description: "Run the model" }],
                currentStep: 0
            };
        }

        return { steps: [{ title: "Unknown", description: "Unknown step" }], currentStep: 0 };
    }, [flow, step]);

    // Check if model has been built using store predictions
    const hasModelBeenBuilt = useMemo(() => {
        if (flow !== Flows.BUILD) return false;
        return (
            Object.keys(predictionsForFileAndFrameSegment).length > 0 &&
            Object.values(predictionsForFileAndFrameSegment).some(
                (predictionsForFile) => Object.keys(predictionsForFile).length > 0
            )
        );
    }, [flow, predictionsForFileAndFrameSegment]);

    const handleStepClick = useCallback(
        (stepIndex: number) => {
            if (flow === Flows.BUILD) {
                if (flowLocked) {
                    return;
                }

                const canNavigateToPreviewUse =
                    hasModelBeenBuilt || step === BuildSteps.REVIEW || step === BuildSteps.PREVIEW;

                if ((stepIndex === 1 || stepIndex === 2) && !canNavigateToPreviewUse) {
                    return;
                }

                if (stepIndex === 0) {
                    if (hasModelBeenBuilt) {
                        setShowRelabelConfirmation(true);
                    } else {
                        setStep({ flow: Flows.BUILD, step: BuildSteps.PROMPT });
                    }
                } else if (stepIndex === 1) {
                    setStep({ flow: Flows.BUILD, step: BuildSteps.REVIEW });
                } else if (stepIndex === 2) {
                    handleCreateRapidWorkflow();
                }
            } else if (flow === Flows.DEPLOY) {
                if (stepIndex === 2 && !isEvaluateReady) return;
                const deployStepMap = [DeploySteps.DEPLOY, DeploySteps.IMPROVE, DeploySteps.MODEL];
                setStep({ flow: Flows.DEPLOY, step: deployStepMap[stepIndex] });
            }
        },
        [
            flow,
            flowLocked,
            handleCreateRapidWorkflow,
            hasModelBeenBuilt,
            isEvaluateReady,
            setStep,
            step
        ]
    );

    if (!steps || steps.length === 0) {
        return null;
    }

    const workflowUrl = hasValidWorkflowCredentials
        ? `${projectSelector.appUrl(false, true)}/${workflowCredentials.workspaceUrl}/workflows/edit/${workflowCredentials.workflowUrl}`
        : "#";

    const modelNameContent = (
        <>
            <span className="truncate">{workflow?.name || "Workflow"}</span>
            <i className="far fa-angle-right text-sm" />
        </>
    );

    const getStepProps = (index: number) => {
        const isActive = index === currentStep;
        const isCompleted = isPostBuild
            ? false // No completed state in deploy model context
            : index < currentStep;

        let isDisabled = false;
        if (flow === Flows.DEPLOY) {
            if (index === 2) {
                isDisabled = !isEvaluateReady;
            }
        } else if (flow === Flows.BUILD) {
            if (index === 0) {
                isDisabled = flowLocked;
            } else if (index === 1) {
                const canNavigateToPreviewUse =
                    hasModelBeenBuilt || step === BuildSteps.REVIEW || step === BuildSteps.PREVIEW;
                isDisabled = !canNavigateToPreviewUse;
            } else if (index === 2) {
                const canNavigateToPreviewUse =
                    hasModelBeenBuilt || step === BuildSteps.REVIEW || step === BuildSteps.PREVIEW;
                isDisabled = !canNavigateToPreviewUse || creatingRapidWorkflow;
            }
        }

        let iconClass: string;
        let tooltip: string | undefined;

        if (isPostBuild) {
            // Deploy model logic
            if (isActive) {
                iconClass = "fas fa-circle-dot text-brand";
            } else if (isTraining && index === 1) {
                // Training state (Improve step only)
                iconClass = "fas fa-spinner fa-spin";
                tooltip = "Retraining model...";
            } else if (newDataReady && index === 1) {
                // Data ready to review (Improve step only)
                iconClass = "fas fa-circle-small text-brand";
                tooltip = "Data ready to review";
            } else if (hasUnseenModelChange && index === 2) {
                // New version available (Evaluate step only)
                iconClass = "fas fa-circle-small text-brand";
                tooltip = "New version ready";
            } else if (!isEvaluateReady && index === 2) {
                iconClass = "far fa-circle text-gray-400";
                tooltip = "Use your model to start gathering data for evaluation";
            } else {
                // Inactive step
                iconClass = "far fa-circle text-gray-400";
            }
        } else {
            // Original logic
            const isBuildGenerating =
                !isPostBuild &&
                ((index === 0 && flowLocked) || (index === 2 && creatingRapidWorkflow));

            if (isBuildGenerating) {
                iconClass = "fas fa-spinner fa-spin text-brand";
            } else if (isActive) {
                iconClass = "fas fa-circle-dot text-brand";
            } else if (isCompleted) {
                iconClass = "fas fa-check text-tertiary";
            } else {
                iconClass = "far fa-circle text-gray-400";
            }
        }

        return { isActive, isCompleted, isDisabled, iconClass, tooltip };
    };

    return (
        <>
            <div className="flex h-12 items-center justify-between gap-4 border-b bg-white px-4">
                <div className="flex items-center gap-1">
                    {isPostBuild &&
                        (hasValidWorkflowCredentials ? (
                            <a
                                href={workflowUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={twClassNames(
                                    STEP_BASE_CLASSES,
                                    STEP_HOVER_CLASSES,
                                    "max-w-60"
                                )}
                            >
                                {modelNameContent}
                            </a>
                        ) : (
                            <span
                                className={twClassNames(
                                    STEP_BASE_CLASSES,
                                    STEP_HOVER_CLASSES,
                                    "max-w-60 cursor-default"
                                )}
                            >
                                {modelNameContent}
                            </span>
                        ))}
                    {steps.map((stepItem, index) => {
                        const { isActive, isCompleted, isDisabled, iconClass, tooltip } =
                            getStepProps(index);

                        return (
                            <React.Fragment key={index}>
                                <Step
                                    title={stepItem.title}
                                    description={stepItem.description}
                                    isActive={isActive}
                                    isCompleted={isCompleted}
                                    isDisabled={isDisabled}
                                    isClickable={isPostBuild}
                                    iconClass={iconClass}
                                    tooltip={tooltip}
                                    onClick={() => handleStepClick(index)}
                                />
                                {index < steps.length - 1 && (
                                    <div
                                        className={
                                            isPostBuild
                                                ? "h-px w-1 bg-white"
                                                : "h-px w-10 bg-gray-300"
                                        }
                                    ></div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>

                <FeedbackForm />
            </div>

            <RelabelConfirmationModal
                isOpen={showRelabelConfirmation}
                onClose={() => setShowRelabelConfirmation(false)}
                onConfirm={() => setShowRelabelConfirmation(false)}
                onCancel={() => {
                    setShowRelabelConfirmation(false);
                    setStep({ flow: Flows.BUILD, step: BuildSteps.PROMPT });
                }}
            />
        </>
    );
};

export default FlowStepper;
